import React, { useEffect, useState } from "react";
import ReportsHelpers from "../../utils/ReportsHelpers";
import ReportCount from "../reports/ReportCount";
import Tooltip from "../shared/Tooltip";
import { reportingGoals } from "../../src/reportingGoals";

const cardClassName =
  "min-w-[175px] flex-1 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow";

const TRACK_REVENUE_URL =
  "https://support.convertflow.com/en/articles/8573269-attributing-revenue-to-convertflow-campaigns";

const selectorClassName =
  "form-control !text-xl border-solid !border-b-[1px] !border-slate-200 !rounded-md !bg-transparent !shadow-none";

const AccountDashboardReport = (props) => {
  const accountId = props.account.id;
  const reportsPath = props.reportsPath;
  const websiteIds = props.websiteIds || [];
  const goals = reportingGoals(props.reportingGoals);
  const checkoutsWebsiteIds = goals.checkoutsWebsiteIds || websiteIds;
  const bookingsWebsiteIds = goals.bookingsWebsiteIds || websiteIds;
  const customGoalWebsiteIds = goals.customGoalWebsiteIds || websiteIds;
  const rangeStart = props.rangeStart;
  const rangeEnd = props.rangeEnd;
  const timeframeLabel = props.timeframeLabel || "Last 30 Days";
  const insightsUrl = props.insightsUrl;
  const showSeeAll = props.showSeeAll === true;
  const timeframeOptions = props.timeframeOptions || [];
  const showTimeframeSelector =
    props.showTimeframeSelector === true && timeframeOptions.length > 0;
  const initialTimeframe =
    props.selectedTimeframe ||
    (props.startDate || props.endDate ? "custom" : "last_30_days");
  const [selectedTimeframe, setSelectedTimeframe] = useState(initialTimeframe);
  const currency = props.currency || "$";
  const monthlySpend = Number(props.monthlySpend) || 0;
  const showRoi = props.showRoi !== false;
  const fallbackRoiRange = (() => {
    const end = new Date();
    return {
      start: new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      end: end.toISOString(),
    };
  })();
  const roiRangeStart = props.roiRangeStart || fallbackRoiRange.start;
  const roiRangeEnd = props.roiRangeEnd || fallbackRoiRange.end;

  const navigateToTimeframe = (timeframe, startDate, endDate) => {
    if (!insightsUrl) {
      return;
    }

    const url = new URL(insightsUrl, window.location.origin);
    url.searchParams.set("timeframe", timeframe);
    if (timeframe === "custom") {
      if (startDate) {
        url.searchParams.set("start_date", startDate);
      }
      if (endDate) {
        url.searchParams.set("end_date", endDate);
      }
    }
    window.location.href = url.toString();
  };
  const comparisonRange = ReportsHelpers.getComparisonRange(
    rangeStart,
    rangeEnd
  );
  const [roi, setRoi] = useState(null);
  const [roiRevenue, setRoiRevenue] = useState(null);

  const reportFilter = (extra = {}) => ({
    website_id: websiteIds,
    range_start: rangeStart,
    range_end: rangeEnd,
    ...extra,
  });

  const comparisonFilter = (extra = {}) =>
    comparisonRange
      ? {
          website_id: websiteIds,
          range_start: comparisonRange.range_start,
          range_end: comparisonRange.range_end,
          ...extra,
        }
      : null;

  useEffect(() => {
    if (!showRoi || !goals.checkouts) {
      return;
    }
    let cancelled = false;
    const payload = {
      report_type: "revenue",
      report: {
        website_id: checkoutsWebsiteIds,
        range_start: roiRangeStart,
        range_end: roiRangeEnd,
        event_type: "Order",
        contactable_type: "Cta",
        contactable_id: null,
      },
    };

    fetch(`${reportsPath}?` + $.param(payload))
      .then((response) => response.json())
      .then((json) => {
        if (cancelled) return;
        const revenue = parseFloat(json) || 0;
        setRoiRevenue(revenue);
        setRoi(monthlySpend > 0 ? revenue / monthlySpend : null);
      })
      .catch(() => {
        if (!cancelled) {
          setRoi(null);
          setRoiRevenue(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    showRoi,
    reportsPath,
    checkoutsWebsiteIds,
    roiRangeStart,
    roiRangeEnd,
    monthlySpend,
    goals.checkouts,
  ]);

  const formatRoi = (value) => {
    if (roiRevenue === 0) {
      return "-";
    }
    if (value === null || Number.isNaN(value)) {
      return "—";
    }

    return `${value.toLocaleString(undefined, {
      maximumFractionDigits: 1,
    })}x`;
  };

  const formatRoiRevenue = (value) => {
    if (value === null || Number.isNaN(value)) {
      return null;
    }

    return `${currency}${value.toLocaleString()}`;
  };

  const roiCardInner = (
    <div className="row hard-center">
      <div className="row hard-center">
        <span className="field-title">30 Day ROI</span>
        <Tooltip
          tooltip="Attributed funnel revenue divided by monthly ConvertFlow spend for the last 30 days."
          inline={true}
          hover={true}
        />
      </div>
      <div className="row hard-center gap-2">
        <h4 className="report-count bold" style={{ margin: "5px 0px 0px" }}>
          {formatRoi(roi)}
        </h4>
      </div>
      <div className="row hard-center" style={{ marginTop: "6px" }}>
        {roiRevenue === 0 ? (
          <a
            href={TRACK_REVENUE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xl !text-blue-600 hover:!text-blue-600 no-underline whitespace-nowrap"
          >
            Track revenue →
          </a>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded text-base font-medium bg-gray-100 text-gray-800">
            {formatRoiRevenue(roiRevenue) || (
              <span
                className="cf-loading-spinner reports-spinner"
                style={{
                  width: "12px",
                  height: "12px",
                  marginRight: "4px",
                }}
              ></span>
            )}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="row hard-center smooth WebsiteConversionReport pt-6 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="bold inline">Account Performance</h4>
          {!showTimeframeSelector && (
            <span className="light text-xl ml-3">{timeframeLabel}</span>
          )}
        </div>
        <div
          className="flex items-center gap-2 flex-wrap"
          style={{ flexShrink: 0 }}
        >
          {showTimeframeSelector && (
            <>
              <select
                name="timeframe"
                aria-label="Account performance timeframe"
                className={selectorClassName}
                value={selectedTimeframe}
                onChange={(event) => {
                  const nextTimeframe = event.target.value;
                  setSelectedTimeframe(nextTimeframe);
                  if (nextTimeframe !== "custom") {
                    navigateToTimeframe(nextTimeframe);
                  }
                }}
                style={{ width: "155px", height: "35px" }}
              >
                {timeframeOptions.map((option) => (
                  <option
                    key={`account-performance-timeframe-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedTimeframe === "custom" && (
                <form
                  action={insightsUrl}
                  method="get"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    navigateToTimeframe(
                      "custom",
                      formData.get("start_date"),
                      formData.get("end_date")
                    );
                  }}
                  className="flex items-center gap-2"
                  style={{ margin: 0 }}
                >
                  <input type="hidden" name="timeframe" value="custom" />
                  <input
                    type="date"
                    name="start_date"
                    aria-label="Start date"
                    defaultValue={props.startDate}
                    className={`${selectorClassName} !text-slate-500`}
                    style={{ width: "110px", height: "35px" }}
                  />
                  <input
                    type="date"
                    name="end_date"
                    aria-label="End date"
                    defaultValue={props.endDate}
                    className={`${selectorClassName} !text-slate-500`}
                    style={{ width: "110px", height: "35px" }}
                  />
                  <button
                    type="submit"
                    className="btn black-button"
                    style={{ height: "35px" }}
                  >
                    Apply
                  </button>
                </form>
              )}
            </>
          )}
          {showSeeAll && insightsUrl && (
            <a
              href={insightsUrl}
              className="text-xl text-black hover:text-blue-600 no-underline whitespace-nowrap"
              style={{ flexShrink: 0 }}
            >
              Account Insights →
            </a>
          )}
        </div>
      </div>
      <div className="mt-5 flex flex-row items-stretch justify-start gap-3 overflow-x-auto w-full">
        <a href={insightsUrl} className={cardClassName}>
          <ReportCount
            label="Total Views"
            tooltip="Total count of all views of all campaigns across all websites in this account. Viewing a campaign 2 times counts as 2 views. Viewing 2 funnel steps count as 2 views."
            tooltipHover={true}
            path={reportsPath}
            payload={{
              report_type: "count",
              report: reportFilter({
                event_type: "View",
                variant_id: null,
                broadcast_id: null,
              }),
            }}
            comparison_payload={
              comparisonFilter({
                event_type: "View",
                variant_id: null,
                broadcast_id: null,
              })
                ? {
                    report_type: "count",
                    report: comparisonFilter({
                      event_type: "View",
                      variant_id: null,
                      broadcast_id: null,
                    }),
                  }
                : null
            }
          />
        </a>
        <a href={insightsUrl} className={cardClassName}>
          <ReportCount
            label="Total Conversions"
            tooltip="Total count of all conversions of all campaigns across all websites in this account. Converting 2 times counts as 2 conversions."
            tooltipHover={true}
            path={reportsPath}
            payload={{
              report_type: "count",
              report: reportFilter({
                event_type: "Complete",
                variant_id: null,
                broadcast_id: null,
              }),
            }}
            comparison_payload={
              comparisonFilter({
                event_type: "Complete",
                variant_id: null,
                broadcast_id: null,
              })
                ? {
                    report_type: "count",
                    report: comparisonFilter({
                      event_type: "Complete",
                      variant_id: null,
                      broadcast_id: null,
                    }),
                  }
                : null
            }
          />
        </a>
        <a href={insightsUrl} className={cardClassName}>
          <ReportCount
            id={`account-${accountId}-subscribes`}
            label="New Contacts"
            tooltip="Total count of all new contacts created across all websites in this account."
            tooltipHover={true}
            path={reportsPath}
            loader={true}
            left={true}
            payload={{
              report_type: "distinct_count",
              report: reportFilter({
                event_type: "Contact Created",
              }),
            }}
            comparison_payload={
              comparisonFilter({
                event_type: "Contact Created",
              })
                ? {
                    report_type: "distinct_count",
                    report: comparisonFilter({
                      event_type: "Contact Created",
                    }),
                  }
                : null
            }
          />
        </a>
        {goals.checkouts && (
          <a href={insightsUrl} className={cardClassName}>
            <ReportCount
              id={`account-${accountId}-orders`}
              label="Total Orders"
              tooltip="Orders attributed to ConvertFlow campaigns across all websites in this account."
              tooltipHover={true}
              path={reportsPath}
              payload={{
                report_type: "count",
                report: reportFilter({
                  website_id: checkoutsWebsiteIds,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                }),
              }}
              comparison_payload={
                comparisonFilter({
                  website_id: checkoutsWebsiteIds,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                })
                  ? {
                      report_type: "count",
                      report: comparisonFilter({
                        website_id: checkoutsWebsiteIds,
                        event_type: "Order",
                        contactable_type: "Cta",
                        contactable_id: null,
                      }),
                    }
                  : null
              }
            />
          </a>
        )}
        {goals.checkouts && (
          <a href={insightsUrl} className={cardClassName}>
            <ReportCount
              id={`account-${accountId}-revenue`}
              label="Total Revenue"
              tooltip="Attributed funnel revenue across all websites in this account."
              tooltipHover={true}
              path={reportsPath}
              currency={currency}
              payload={{
                report_type: "revenue",
                report: reportFilter({
                  website_id: checkoutsWebsiteIds,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                }),
              }}
              comparison_payload={
                comparisonFilter({
                  website_id: checkoutsWebsiteIds,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                })
                  ? {
                      report_type: "revenue",
                      report: comparisonFilter({
                        website_id: checkoutsWebsiteIds,
                        event_type: "Order",
                        contactable_type: "Cta",
                        contactable_id: null,
                      }),
                    }
                  : null
              }
            />
          </a>
        )}
        {goals.bookings && (
          <a href={insightsUrl} className={cardClassName}>
            <ReportCount
              id={`account-${accountId}-bookings`}
              label="Bookings"
              tooltip="Scheduler bookings from ConvertFlow campaigns across all websites in this account."
              tooltipHover={true}
              path={reportsPath}
              payload={{
                report_type: "distinct_count",
                report: reportFilter({
                  website_id: bookingsWebsiteIds,
                  event_type: "Booking",
                  contactable_type: null,
                  contactable_id: null,
                  variant_id: null,
                  broadcast_id: null,
                }),
              }}
              comparison_payload={
                comparisonFilter({
                  website_id: bookingsWebsiteIds,
                  event_type: "Booking",
                  contactable_type: null,
                  contactable_id: null,
                  variant_id: null,
                  broadcast_id: null,
                })
                  ? {
                      report_type: "distinct_count",
                      report: comparisonFilter({
                        website_id: bookingsWebsiteIds,
                        event_type: "Booking",
                        contactable_type: null,
                        contactable_id: null,
                        variant_id: null,
                        broadcast_id: null,
                      }),
                    }
                  : null
              }
            />
          </a>
        )}
        {goals.custom && (
          <a href={insightsUrl} className={cardClassName}>
            <ReportCount
              id={`account-${accountId}-custom-goals`}
              label={goals.customGoalLabel}
              tooltip={`${goals.customGoalLabel} tracked across websites in this account.`}
              tooltipHover={true}
              path={reportsPath}
              payload={{
                report_type: "distinct_count",
                report: reportFilter({
                  website_id: customGoalWebsiteIds,
                  event_type: "Custom Goal",
                  contactable_type: null,
                  contactable_id: null,
                  variant_id: null,
                  broadcast_id: null,
                }),
              }}
              comparison_payload={
                comparisonFilter({
                  website_id: customGoalWebsiteIds,
                  event_type: "Custom Goal",
                  contactable_type: null,
                  contactable_id: null,
                  variant_id: null,
                  broadcast_id: null,
                })
                  ? {
                      report_type: "distinct_count",
                      report: comparisonFilter({
                        website_id: customGoalWebsiteIds,
                        event_type: "Custom Goal",
                        contactable_type: null,
                        contactable_id: null,
                        variant_id: null,
                        broadcast_id: null,
                      }),
                    }
                  : null
              }
            />
          </a>
        )}
        {showRoi &&
          goals.checkouts &&
          (roiRevenue === 0 ? (
            <div className={cardClassName}>{roiCardInner}</div>
          ) : (
            <a href={insightsUrl} className={cardClassName}>
              {roiCardInner}
            </a>
          ))}
      </div>
    </div>
  );
};

export default AccountDashboardReport;
